import { FormEvent, useContext } from 'react';

import Grid from '@mui/material/Unstable_Grid2';

import { Form, Formik } from 'formik';

import {
  LinkButton,
  SearchableMultiSelect,
  SubmitButton,
  ButtonsBlock,
  Checkbox,
  DatePickerGroup,
} from 'components/UIComponents';

import { BulkSelectContext } from 'context/BulkSelectContext';
import { FilterContext } from 'context/FilterContext';

import {
  getEstimateOptions,
  getGroupOptions,
  getMarketOptions,
  getOutletOptions,
  getProductOptionsForOrder,
  getSendToContactOptions,
  getUserOptions,
  getWeekStartOptions,
} from 'helpers/collections';
import { nonEmptyValues } from 'helpers/utils';

import { statusOptions } from 'constants/orders';

import { IFilterOptions } from 'interfaces/Order/IFilterOptions';

import { controllWrapperStyles } from './styledComponents';

const emptyValues = {
  isRevised: false,
  statuses: [],
  groupsIds: [],
  productsIds: [],
  createdByIds: [],
  estimates: [],
  outletsIds: [],
  weekStarts: [],
  markets: [],
  groupContacts: [],
  lastSentDateFrom: '',
  lastSentDateTo: '',
};

const OrderFilterForm = () => {
  const { filterOptions, onFilter } = useContext(FilterContext);
  const { onChangeSelectAll } = useContext(BulkSelectContext);

  const initital = filterOptions as IFilterOptions;

  const initialValues = {
    isRevised: initital.isRevised || false,
    statuses: initital.statuses || [],
    groupsIds: initital.groupsIds || [],
    productsIds: initital.productsIds || [],
    outletsIds: initital.outletsIds || [],
    createdByIds: initital.createdByIds || [],
    estimates: initital.estimates || [],
    weekStarts: initital.weekStarts || [],
    markets: initital.markets || [],
    groupContacts: initital.groupContacts || [],
    lastSentDateFrom: initital.lastSentDateFrom || '',
    lastSentDateTo: initital.lastSentDateTo || '',
  };

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize={true}
      onSubmit={(values) => {
        onChangeSelectAll(false);
        const present = nonEmptyValues(values);
        if (present.isRevised === false) delete present.isRevised;

        onFilter(present);
      }}
    >
      {({ values, handleSubmit, resetForm }) => {
        return (
          <Form>
            <Grid container columns={14} rowSpacing={2} columnSpacing={{ xs: 2, lg: 3 }}>
              <Grid xs={2}>
                <SearchableMultiSelect
                  name="groupsIds"
                  label="Group"
                  initial={initialValues.groupsIds}
                  getOptionsList={getGroupOptions}
                  styles={controllWrapperStyles}
                  direction="column"
                />
              </Grid>
              <Grid xs={2}>
                <SearchableMultiSelect
                  name="groupContacts"
                  label="Group Contact"
                  initial={initialValues.groupContacts}
                  getOptionsList={getSendToContactOptions}
                  styles={controllWrapperStyles}
                  emptyOptionLabel="Any Contact"
                  direction="column"
                />
              </Grid>
              <Grid xs={2}>
                <SearchableMultiSelect
                  name="outletsIds"
                  label="Outlet"
                  initial={initialValues.outletsIds}
                  getOptionsList={getOutletOptions}
                  styles={controllWrapperStyles}
                  direction="column"
                />
              </Grid>
              <Grid xs={2}>
                <SearchableMultiSelect
                  name="markets"
                  label="Market"
                  initial={initialValues.markets}
                  getOptionsList={getMarketOptions}
                  styles={controllWrapperStyles}
                  direction="column"
                />
              </Grid>
              <Grid xs={2}>
                <SearchableMultiSelect
                  name="productsIds"
                  label="Product"
                  getOptionsList={getProductOptionsForOrder}
                  initial={initialValues.productsIds}
                  styles={controllWrapperStyles}
                  emptyOptionLabel="Any Product"
                  direction="column"
                />
              </Grid>
              <Grid xs={2}>
                <SearchableMultiSelect
                  name="weekStarts"
                  label="Week"
                  initial={initialValues.weekStarts}
                  getOptionsList={getWeekStartOptions}
                  styles={controllWrapperStyles}
                  direction="column"
                />
              </Grid>
              <Grid xs={2}>
                <Checkbox
                  styles={{
                    wrapper: {
                      margin: '0 !important',
                      marginTop: '18px !important',
                    },
                  }}
                  view="checkboxFirst"
                  name="isRevised"
                  label="Show Revised Only"
                />
              </Grid>
            </Grid>
            <Grid container columns={14} rowSpacing={2} columnSpacing={{ xs: 2, lg: 3 }}>
              <Grid xs={2}>
                <SearchableMultiSelect
                  name="estimates"
                  label="Estimate"
                  initial={initialValues.estimates}
                  getOptionsList={getEstimateOptions}
                  styles={controllWrapperStyles}
                  direction="column"
                />
              </Grid>
              <Grid xs={2}>
                <SearchableMultiSelect
                  name="statuses"
                  label="Status"
                  initial={initialValues.statuses}
                  getOptionsList={() => Promise.resolve(statusOptions)}
                  styles={controllWrapperStyles}
                  direction="column"
                />
              </Grid>
              <Grid xs={2}>
                <SearchableMultiSelect
                  name="createdByIds"
                  label="Order Creator"
                  initial={initialValues.createdByIds}
                  getOptionsList={getUserOptions}
                  styles={controllWrapperStyles}
                  emptyOptionLabel="Any Creator"
                  direction="column"
                />
              </Grid>
              <Grid xs={4}>
                <DatePickerGroup
                  startProps={{
                    name: 'lastSentDateFrom',
                    direction: 'column',
                    maxDate: values.lastSentDateTo,
                  }}
                  endProps={{
                    name: 'lastSentDateTo',
                    direction: 'column',
                    minDate: values.lastSentDateFrom,
                  }}
                  label='Last Sent'
                  connector='to'
                  styles={controllWrapperStyles}
                  direction='column'
                />
              </Grid>
              <Grid display="flex" alignItems="flex-end" justifyContent="flex-end" marginBottom="2px" xs={4}>
                <ButtonsBlock>
                  <LinkButton
                    onClick={() => {
                      resetForm({ values: emptyValues });
                      onChangeSelectAll(false);
                      onFilter({});
                    }}
                  >
                    Clear filter
                  </LinkButton>
                  <SubmitButton onClick={(values: FormEvent<HTMLFormElement>) => handleSubmit(values)}>
                    Apply Filter
                  </SubmitButton>
                </ButtonsBlock>
              </Grid>
            </Grid>
          </Form>
        );
      }}
    </Formik>
  );
};

export default OrderFilterForm;
